<template>
  <div class="page-container coupon-editor">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar cupón" : "Crear cupón" }}
      </h1>
    </div>
    <div class="container px-0">
      <div class="row justify-content-center">
        <div class="col-12 col-lg-9">
          <b-card tag="article" no-body style="max-width: 760px" class="custom-card mx-auto">
            <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
              <h3>{{ editing ? "Datos del cupón" : "Datos del nuevo cupón" }}</h3>
            </b-card-header>
            <b-card-body class="custom-card-body p-0">
              <form class="custom-form" @submit.prevent="save" novalidate>
                <!-- 1. Información básica -->
                <div class="form-section">
                  <h2 class="custom-card-subtitle">Información básica</h2>
                  <p class="custom-card-description">Código que ingresará el cliente al pagar</p>

                  <div class="form-group">
                    <label for="code">Código *</label>
                    <div class="d-flex">
                      <FormInput
                        id="code"
                        type="text"
                        maxlength="50"
                        placeholder="Ej. BIENVENIDA10"
                        class="flex-1 text-uppercase"
                        :show-error="$v.coupon.code.$error && !$v.coupon.code.required"
                        v-model="coupon.code"
                      />
                      <button type="button" class="button button-light ml-2" @click="generateCode">
                        Generar
                      </button>
                    </div>
                    <form-error
                      message="Código es requerido"
                      v-if="$v.coupon.code.$error && !$v.coupon.code.required"
                    ></form-error>
                  </div>

                  <div class="form-group mb-0">
                    <label for="description">Descripción interna</label>
                    <textarea
                      id="description"
                      maxlength="500"
                      placeholder="Solo visible en el admin"
                      class="custom-form-control"
                      v-model="coupon.description"
                    ></textarea>
                  </div>
                </div>

                <!-- 2. Tipo de descuento -->
                <div class="form-section">
                  <h2 class="custom-card-subtitle">Tipo de descuento</h2>

                  <div class="form-group">
                    <label>Tipo *</label>
                    <b-form-radio-group
                      v-model="coupon.type"
                      :options="typeOptions"
                      value-field="key"
                      text-field="label"
                    ></b-form-radio-group>
                  </div>

                  <div class="row" v-if="!isFreeShipping">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="value">Valor *</label>
                        <b-input-group
                          :prepend="isPercentage ? null : 'S/'"
                          :append="isPercentage ? '%' : null"
                          class="d-flex flex-nowrap"
                        >
                          <FormInput
                            id="value"
                            type="number"
                            min="0"
                            step="0.01"
                            :placeholder="isPercentage ? '20' : '15.00'"
                            :show-error="$v.coupon.value.$error"
                            v-model="coupon.value"
                          />
                        </b-input-group>
                        <form-error
                          message="Valor es requerido"
                          v-if="$v.coupon.value.$error && !$v.coupon.value.required"
                        ></form-error>
                        <form-error
                          message="El valor debe ser mayor a 0"
                          v-else-if="$v.coupon.value.$error && !$v.coupon.value.positive"
                        ></form-error>
                        <form-error
                          message="El porcentaje no puede ser mayor a 100"
                          v-else-if="$v.coupon.value.$error && !$v.coupon.value.maxPercentage"
                        ></form-error>
                      </div>
                    </div>

                    <div class="col-md-6" v-if="isPercentage">
                      <div class="form-group">
                        <label for="maxDiscount">Descuento máximo</label>
                        <b-input-group prepend="S/" class="d-flex flex-nowrap">
                          <FormInput
                            id="maxDiscount"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="50.00"
                            v-model="coupon.maxDiscount"
                          />
                        </b-input-group>
                      </div>
                    </div>
                  </div>
                  <p class="custom-card-description mb-0" v-else>
                    El costo de envío del pedido pasará a S/ 0.00
                  </p>
                </div>

                <!-- 3. Aplicación -->
                <div class="form-section">
                  <h2 class="custom-card-subtitle">Aplicación</h2>

                  <div class="form-group">
                    <label>Alcance *</label>
                    <b-form-radio-group
                      v-model="coupon.scope"
                      :options="scopeOptions"
                      value-field="key"
                      text-field="label"
                      stacked
                    ></b-form-radio-group>
                  </div>

                  <!-- Categorías / Marcas: lista con buscador -->
                  <div class="form-group mb-0" v-if="isCategoriesScope || isBrandsScope">
                    <label>{{ isCategoriesScope ? "Categorías" : "Marcas" }} *</label>
                    <FormInput
                      type="text"
                      :placeholder="isCategoriesScope ? 'Buscar categoría' : 'Buscar marca'"
                      v-model="scopeFilter"
                    />
                    <div class="scope-options mt-2">
                      <b-form-checkbox
                        v-for="option in filteredScopeOptions"
                        :key="option.id"
                        :value="option.id"
                        v-model="selected[coupon.scope]"
                        :class="{ 'pl-4': option.child }"
                      >
                        {{ option.name }}
                      </b-form-checkbox>
                      <p class="text-muted m-0" v-if="!filteredScopeOptions.length">Sin resultados</p>
                    </div>
                    <small class="text-muted">{{ selected[coupon.scope].length }} seleccionadas</small>
                  </div>

                  <!-- Productos: buscador + seleccionados -->
                  <div class="form-group mb-0" v-if="isProductsScope">
                    <label>Productos *</label>
                    <FormInput
                      type="text"
                      placeholder="Buscar por nombre o código"
                      v-model="productSearch"
                    />
                    <ul class="list-unstyled scope-options mt-2" v-if="productResults.length">
                      <li
                        v-for="product in productResults"
                        :key="product.id"
                        class="d-flex justify-content-between align-items-center py-1"
                      >
                        <span>{{ product.name }} <small class="text-muted">({{ product.code }})</small></span>
                        <button
                          type="button"
                          class="button button-light py-0"
                          :disabled="isProductSelected(product.id)"
                          @click="addProduct(product)"
                        >
                          {{ isProductSelected(product.id) ? "Agregado" : "Agregar" }}
                        </button>
                      </li>
                    </ul>
                    <div class="mt-2">
                      <span v-for="product in selectedProducts" :key="product.id" class="filter-item">
                        {{ product.name }}
                        <button
                          type="button"
                          class="text-white p-0 bg-transparent border-0 ml-2"
                          @click="removeProduct(product.id)"
                        >
                          <i class="ri-close-circle-line"></i>
                        </button>
                      </span>
                    </div>
                  </div>

                  <form-error
                    message="Seleccione al menos un elemento para el alcance del cupón"
                    v-if="$v.selectedScopeIds.$error && !$v.selectedScopeIds.scopeItems"
                  ></form-error>
                </div>

                <!-- 4. Condiciones -->
                <div class="form-section">
                  <h2 class="custom-card-subtitle">Condiciones</h2>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="minPurchaseAmount">Monto mínimo de compra</label>
                        <b-input-group prepend="S/" class="d-flex flex-nowrap">
                          <FormInput
                            id="minPurchaseAmount"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            v-model="coupon.minPurchaseAmount"
                          />
                        </b-input-group>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="minProductQuantity">Cantidad mínima de productos</label>
                        <FormInput
                          id="minProductQuantity"
                          type="number"
                          min="0"
                          step="1"
                          placeholder="0"
                          v-model="coupon.minProductQuantity"
                        />
                      </div>
                    </div>
                  </div>
                  <b-form-checkbox v-model="coupon.firstPurchaseOnly">Solo primera compra</b-form-checkbox>
                  <b-form-checkbox v-model="coupon.registeredOnly" class="mt-2">Solo clientes registrados</b-form-checkbox>
                </div>

                <!-- 5. Límites de uso -->
                <div class="form-section">
                  <h2 class="custom-card-subtitle">Límites de uso</h2>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group mb-md-0">
                        <label for="totalUsageLimit">Usos totales</label>
                        <FormInput
                          id="totalUsageLimit"
                          type="number"
                          min="1"
                          step="1"
                          placeholder="Ilimitado"
                          v-model="coupon.totalUsageLimit"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group mb-0">
                        <label for="perCustomerLimit">Usos por cliente</label>
                        <FormInput
                          id="perCustomerLimit"
                          type="number"
                          min="1"
                          step="1"
                          placeholder="Ilimitado"
                          v-model="coupon.perCustomerLimit"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 6. Vigencia -->
                <div class="form-section">
                  <h2 class="custom-card-subtitle">Vigencia</h2>
                  <p class="custom-card-description">Déjelas vacías para que el cupón sea válido desde ya y no expire</p>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group mb-md-0">
                        <label for="startDate">Fecha de inicio</label>
                        <input id="startDate" type="datetime-local" class="custom-form-control" v-model="coupon.startDate" />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group mb-0">
                        <label for="endDate">Fecha de fin</label>
                        <input id="endDate" type="datetime-local" class="custom-form-control" v-model="coupon.endDate" />
                        <form-error
                          message="La fecha de fin debe ser posterior a la de inicio"
                          v-if="$v.coupon.endDate.$error && !$v.coupon.endDate.afterStart"
                        ></form-error>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 7. Opciones -->
                <div class="form-section border-bottom-0">
                  <h2 class="custom-card-subtitle">Opciones</h2>
                  <b-form-checkbox v-model="coupon.combinableWithDiscount">
                    Combinable con descuento de producto
                  </b-form-checkbox>
                  <p class="custom-card-description mt-1 mb-0">
                    Si se desmarca, el cupón no se aplica a productos que ya tienen descuento
                  </p>

                  <!-- Estadísticas: solo lectura, al editar un cupón ya usado.
                       Oculta temporalmente; la condición original es v-if="editing && stats && stats.usageCount > 0" -->
                  <div class="coupon-stats mt-4" v-if="false">
                    <h2 class="custom-card-subtitle">Estadísticas de uso</h2>
                    <div class="row">
                      <div class="col-md-4">
                        <label class="label">Veces usado</label>
                        <p class="text mb-0">{{ stats.usageCount }}</p>
                      </div>
                      <div class="col-md-4">
                        <label class="label">Monto total descontado</label>
                        <p class="text mb-0" v-decimal:pen="stats.totalDiscountAmount"></p>
                      </div>
                      <div class="col-md-4">
                        <label class="label">Último uso</label>
                        <p class="text mb-0" key="last-used" v-local-date:hour="stats.lastUsedAt" v-if="stats.lastUsedAt"></p>
                        <p class="text mb-0" key="never-used" v-else>-</p>
                      </div>
                    </div>
                  </div>

                  <div class="form-group-buttons text-right">
                    <router-link :to="{ name: 'tools-coupons-list' }" class="button button-light">
                      {{ editing ? "Volver" : "Cancelar" }}
                    </router-link>
                    <button class="button button-primary" type="submit">Guardar</button>
                  </div>
                </div>
              </form>
            </b-card-body>
          </b-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Alert, Constants, FormError, FormInput } from "@wizepe/wize-admin";
import { required, requiredIf } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";

import { Constants as LocalConstants } from "@/core/utils";
import { BrandService, CouponService, ProductService } from "@/core/services";

const { CouponType, CouponScope } = LocalConstants;

// Tipo de ítem que espera el backend según el alcance
const SCOPE_ITEM_TYPES = {
  CATEGORIES: "CATEGORY",
  PRODUCTS: "PRODUCT",
  BRANDS: "BRAND",
};

// Sin 0/O ni 1/I para que el cliente no los confunda al escribirlo
const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

const toNumber = (value) => (value === null || value === undefined || value === "" ? null : Number(value));
const toInteger = (value) => (value === null || value === undefined || value === "" ? null : parseInt(value, 10));
// datetime-local da "YYYY-MM-DDTHH:mm"; el backend (LocalDateTime) lo acepta tal cual
const toDateTime = (value) => (value ? value : null);

export default {
  data() {
    return {
      coupon: {
        code: null,
        description: null,
        type: CouponType.PERCENTAGE.key,
        value: null,
        maxDiscount: null,
        scope: CouponScope.ALL.key,
        minPurchaseAmount: null,
        minProductQuantity: null,
        firstPurchaseOnly: false,
        registeredOnly: false,
        totalUsageLimit: null,
        perCustomerLimit: null,
        startDate: null,
        endDate: null,
        combinableWithDiscount: true,
      },
      // Ids seleccionados por alcance (se conservan si se cambia de alcance y se vuelve)
      selected: {
        CATEGORIES: [],
        PRODUCTS: [],
        BRANDS: [],
      },
      selectedProducts: [],
      productSearch: "",
      productResults: [],
      productSearchTimer: null,
      scopeFilter: "",
      brands: [],
      typeOptions: Object.values(CouponType),
      scopeOptions: Object.values(CouponScope),
      editing: false,
      // Estadísticas de uso (solo lectura, al editar)
      stats: null,
    };
  },
  components: {
    FormError,
    FormInput,
  },
  validations: {
    coupon: {
      code: { required },
      type: { required },
      value: {
        required: requiredIf((coupon) => coupon.type !== CouponType.FREE_SHIPPING.key),
        positive: (value, coupon) =>
          coupon.type === CouponType.FREE_SHIPPING.key || value === null || value === "" || Number(value) > 0,
        maxPercentage: (value, coupon) =>
          coupon.type !== CouponType.PERCENTAGE.key || value === null || value === "" || Number(value) <= 100,
      },
      scope: { required },
      endDate: {
        afterStart: (value, coupon) => !value || !coupon.startDate || value > coupon.startDate,
      },
    },
    selectedScopeIds: {
      scopeItems: (value, vm) => vm.coupon.scope === CouponScope.ALL.key || value.length > 0,
    },
  },
  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    "coupon.scope": function () {
      this.scopeFilter = "";
    },
    productSearch(value) {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(() => this.searchProducts(value), 400);
    },
  },
  computed: {
    ...mapGetters({
      categories: "app/getCategories",
    }),
    isPercentage() {
      return this.coupon.type === CouponType.PERCENTAGE.key;
    },
    isFreeShipping() {
      return this.coupon.type === CouponType.FREE_SHIPPING.key;
    },
    isCategoriesScope() {
      return this.coupon.scope === CouponScope.CATEGORIES.key;
    },
    isBrandsScope() {
      return this.coupon.scope === CouponScope.BRANDS.key;
    },
    isProductsScope() {
      return this.coupon.scope === CouponScope.PRODUCTS.key;
    },
    selectedScopeIds() {
      const list = this.selected[this.coupon.scope];
      return list ? list : [];
    },
    // Categorías: padres y debajo sus hijas (con sangría). Marcas: por nombre
    scopeOptionsList() {
      if (this.isCategoriesScope) {
        const parents = this.categories.filter((c) => !c.parent);
        return parents.reduce((acc, parent) => {
          acc.push({ id: parent.id, name: parent.name, child: false });
          this.categories
            .filter((c) => c.parent && c.parent.id === parent.id)
            .forEach((c) => acc.push({ id: c.id, name: c.name, child: true }));
          return acc;
        }, []);
      }
      if (this.isBrandsScope) {
        return this.brands.map((b) => ({ id: b.id, name: b.name, child: false }));
      }
      return [];
    },
    filteredScopeOptions() {
      const term = (this.scopeFilter || "").toLowerCase().trim();
      if (!term) return this.scopeOptionsList;
      return this.scopeOptionsList.filter((o) => o.name.toLowerCase().includes(term));
    },
  },
  methods: {
    generateCode() {
      let code = "";
      for (let i = 0; i < 8; i++) {
        code += CODE_CHARS.charAt(Math.floor(Math.random() * CODE_CHARS.length));
      }
      this.coupon.code = code;
    },
    async searchProducts(term) {
      if (!term || term.trim().length < 2) {
        this.productResults = [];
        return;
      }
      try {
        const response = await ProductService.list({ search: term.trim(), page: 0, size: 10 });
        this.productResults = response.payload.content;
      } catch (error) {
        console.error(error);
      }
    },
    isProductSelected(id) {
      return this.selected.PRODUCTS.includes(id);
    },
    addProduct(product) {
      if (this.isProductSelected(product.id)) return;
      this.selected.PRODUCTS.push(product.id);
      this.selectedProducts.push({ id: product.id, name: product.name });
    },
    removeProduct(id) {
      this.selected.PRODUCTS = this.selected.PRODUCTS.filter((item) => item !== id);
      this.selectedProducts = this.selectedProducts.filter((item) => item.id !== id);
    },
    getBackendErrors(response) {
      const list = Array.isArray(response.payload) ? response.payload : [];
      const messages = list
        .map((error) => (error.field ? `${error.field}: ${error.defaultMessage}` : error.defaultMessage))
        .filter(Boolean);
      if (messages.length) return messages.join("\n");
      return response.errors && response.errors.message
        ? response.errors.message
        : "No se pudo guardar el cupón, revise los datos";
    },
    buildPayload() {
      const coupon = this.coupon;
      const scopeItemType = SCOPE_ITEM_TYPES[coupon.scope];
      return {
        code: coupon.code ? coupon.code.trim().toUpperCase() : null,
        description: coupon.description,
        type: coupon.type,
        value: this.isFreeShipping ? null : toNumber(coupon.value),
        maxDiscount: this.isPercentage ? toNumber(coupon.maxDiscount) : null,
        scope: coupon.scope,
        minPurchaseAmount: toNumber(coupon.minPurchaseAmount),
        minProductQuantity: toInteger(coupon.minProductQuantity),
        firstPurchaseOnly: coupon.firstPurchaseOnly,
        registeredOnly: coupon.registeredOnly,
        totalUsageLimit: toInteger(coupon.totalUsageLimit),
        perCustomerLimit: toInteger(coupon.perCustomerLimit),
        startDate: toDateTime(coupon.startDate),
        endDate: toDateTime(coupon.endDate),
        combinableWithDiscount: coupon.combinableWithDiscount,
        scopeItems: scopeItemType
          ? this.selectedScopeIds.map((itemId) => ({ itemType: scopeItemType, itemId }))
          : [],
      };
    },
    async save() {
      this.$v.$touch();

      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }

      this.$store.dispatch("app/loading", true);
      try {
        const data = this.buildPayload();
        const response = this.editing
          ? await CouponService.update(this.$route.params.id, data)
          : await CouponService.save(data);

        // Los errores de validación del backend llegan con HTTP 200 y status BAD_REQUEST
        if (response.status !== "OK") {
          Alert.error(this.getBackendErrors(response));
          return;
        }

        Alert.success("Se almacenó la información correctamente");
        if (!this.editing) {
          this.$router.push({
            name: "tools-coupons-edit",
            params: { id: response.payload.id },
          });
          this.editing = true;
        }
      } catch (ex) {
        // p. ej. "Ya existe un cupón con ese código" o valores inválidos
        Alert.error(ex.errors ? ex.errors.message : ex);
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await CouponService.get(id);
        const data = response.payload;

        this.coupon = {
          code: data.code,
          description: data.description,
          type: data.type,
          value: data.value,
          maxDiscount: data.maxDiscount,
          scope: data.scope,
          minPurchaseAmount: data.minPurchaseAmount,
          minProductQuantity: data.minProductQuantity,
          firstPurchaseOnly: !!data.firstPurchaseOnly,
          registeredOnly: !!data.registeredOnly,
          totalUsageLimit: data.totalUsageLimit,
          perCustomerLimit: data.perCustomerLimit,
          // "YYYY-MM-DDTHH:mm:ss" → "YYYY-MM-DDTHH:mm" para datetime-local
          startDate: data.startDate ? data.startDate.slice(0, 16) : null,
          endDate: data.endDate ? data.endDate.slice(0, 16) : null,
          combinableWithDiscount: data.combinableWithDiscount !== false,
        };

        this.stats = {
          usageCount: data.usageCount || 0,
          totalDiscountAmount: data.totalDiscountAmount || 0,
          lastUsedAt: data.lastUsedAt,
        };

        const ids = (data.scopeItems || []).map((item) => item.itemId);
        this.selected = { CATEGORIES: [], PRODUCTS: [], BRANDS: [] };
        if (this.selected[data.scope]) this.selected[data.scope] = ids;

        // Los productos solo vienen como id: se cargan sus nombres para mostrarlos
        if (data.scope === CouponScope.PRODUCTS.key && ids.length) {
          const products = await Promise.all(
            ids.map((productId) => ProductService.get(productId).then((r) => r.payload).catch(() => null))
          );
          this.selectedProducts = ids.map((productId, i) => ({
            id: productId,
            name: products[i] ? products[i].name : `Producto #${productId}`,
          }));
        }

        this.editing = true;
      } catch (ex) {
        let message = ex.errors ? ex.errors.message : ex;
        if (message == "No encontrado") message = Constants.Errors.RECORD_NOT_FOUND;
        Alert.error(message, () => this.$router.push({ name: "tools-coupons-list" }));
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async loadBrands() {
      try {
        const response = await BrandService.listActive();
        this.brands = response.payload;
      } catch (error) {
        console.error(error);
      }
    },
  },
  async created() {
    await Promise.all([this.$store.dispatch("app/loadCategories"), this.loadBrands()]);
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped>
.coupon-editor
  .coupon-stats
    .label
      display: block
      font-size: 12px
      color: #6c757d
      margin-bottom: 2px

    .text
      font-size: 15px
      font-weight: 600
      color: #3e4657

  .scope-options
    max-height: 240px
    overflow-y: auto
    border: 1px solid #e8eaed
    border-radius: 6px
    padding: 8px 12px
</style>
